import { useEffect, useMemo, useState } from 'react'
import { SOURCE_LABELS } from '../api/leadsApi'

/**
 * Generic localStorage-cached "distinct source values" hook, shared by the
 * Leads and Deals Source filters.
 *
 * The Source filter must always show every distinct source that exists in the
 * table — not just the sources present on the current paginated page. This
 * fetches the complete list from a `/sources` endpoint once, caches it in
 * localStorage, and reuses the cache on every page/pagination change.
 *
 * Cache rules:
 *  - Missing cache            → fetch from API, store.
 *  - Hard refresh (reload)    → cache is bypassed once and re-fetched.
 *  - Older than the TTL       → re-fetched.
 *  - Fetch failure            → falls back to stale cache, then to the static
 *                               fallback list, so the filter never goes empty.
 */

/** Default TTL: how long the cached list stays fresh. */
export const SOURCE_CACHE_TTL_MS = 30 * 60 * 1000 // 30 minutes

export interface SourceFilterOption { label: string; value: string }

interface SourceCache {
  sources: string[]
  cachedAt: number
}

function readCache(cacheKey: string): SourceCache | null {
  try {
    const raw = localStorage.getItem(cacheKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed?.sources) || typeof parsed?.cachedAt !== 'number') return null
    return parsed as SourceCache
  } catch {
    return null
  }
}

function writeCache(cacheKey: string, sources: string[]): void {
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ sources, cachedAt: Date.now() } satisfies SourceCache))
  } catch {
    /* storage full/unavailable — non-fatal, we just re-fetch next time */
  }
}

/** True when this page load was a hard refresh (browser reload). */
function isHardReload(): boolean {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    return nav?.type === 'reload'
  } catch {
    return false
  }
}

// Invalidate each cache key at most once per page load on hard refresh, so a
// reload always pulls a fresh list without every remount re-fetching.
const hardReloadHandledKeys = new Set<string>()

export function useCachedSourceOptions(
  cacheKey: string,
  fetchSources: () => Promise<string[]>,
  fallbackSources: readonly string[] = [],
  ttlMs: number = SOURCE_CACHE_TTL_MS,
) {
  const [sources, setSources] = useState<string[]>(() => readCache(cacheKey)?.sources ?? [])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!hardReloadHandledKeys.has(cacheKey)) {
      hardReloadHandledKeys.add(cacheKey)
      if (isHardReload()) localStorage.removeItem(cacheKey)
    }

    const cached = readCache(cacheKey)
    if (cached && Date.now() - cached.cachedAt < ttlMs) {
      setSources(cached.sources)
      return
    }

    let cancelled = false
    setIsLoading(true)
    fetchSources()
      .then((list) => {
        if (cancelled) return
        writeCache(cacheKey, list)
        setSources(list)
      })
      .catch(() => {
        // Keep whatever we already have (stale cache / static fallback below).
        if (!cancelled && cached) setSources(cached.sources)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, ttlMs])

  const options: SourceFilterOption[] = useMemo(() => {
    // Fall back to the static list until the API list is available, so the
    // filter is usable on first paint even with an empty cache.
    const list = sources.length > 0 ? sources : [...fallbackSources]
    return list.map((s) => ({ label: SOURCE_LABELS[s] ?? s, value: s }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources])

  return { options, sources, isLoading }
}
